import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { PackageDetailRequestViewModel } from '../models/PackageDetailRequestViewModel';
import type { PackageDetailViewModel } from '../models/PackageDetailViewModel';
import type { PackageListViewModel } from '../models/PackageListViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class PackagesService {
  /**
   * Detail
   * Gets a specific hotel by slug
   * @returns PackageDetailViewModel Success
   * @throws ApiError
   */
  public static async getPackage(
    {
      slug,
      body,
    }: {
      slug: string;
      body?: PackageDetailRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PackageDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PackageDetailViewModel>({
      method: 'POST',
      url: `/v2/packages/${slug}`,
      path: {
        ...(slug ? { slug: slug } : {}),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * List
   * Gets all packages by slug
   * @returns PackageListViewModel Success
   * @throws ApiError
   */
  public static async listPackages(
    {
      slug,
      _in,
      out,
      sort,
      region,
      theme,
      popularity,
      chain,
      feature,
      boardType,
      point,
      refundable,
      status,
      priceMin,
      priceMax,
      room,
      keywords,
      departure,
      departureIsCity,
      arrival,
      arrivalIsCity,
      oneWay,
      direct,
      page,
      size,
      basketId,
    }: {
      slug?: string;
      _in?: string;
      out?: string;
      /**
       * Sort: popularity, priceasc or pricedesc, Default: popularity
       */
      sort?: any;
      region?: Array<number>;
      theme?: Array<number>;
      popularity?: Array<number>;
      chain?: Array<number>;
      feature?: Array<number>;
      boardType?: Array<number>;
      point?: Array<number>;
      refundable?: boolean;
      status?: boolean;
      priceMin?: number;
      priceMax?: number;
      room?: string;
      keywords?: string;
      departure?: string;
      /**
       * Departure is city
       */
      departureIsCity?: boolean;
      /**
       * Arrival airport
       */
      arrival?: string;
      /**
       * Arrival is city
       */
      arrivalIsCity?: boolean;
      /**
       * One way or rondtrip
       */
      oneWay?: boolean;
      /**
       * Direct or with stops
       */
      direct?: boolean;
      page?: number;
      size?: number;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PackageListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PackageListViewModel>({
      method: 'GET',
      url: `/v2/packages`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(slug ? { slug: slug } : {}),
        ...(_in ? { in: _in } : {}),
        ...(out ? { out: out } : {}),
        ...(sort ? { sort: sort } : {}),
        ...(region ? { region: region } : {}),
        ...(theme ? { theme: theme } : {}),
        ...(popularity ? { popularity: popularity } : {}),
        ...(chain ? { chain: chain } : {}),
        ...(feature ? { feature: feature } : {}),
        ...(boardType ? { boardType: boardType } : {}),
        ...(point ? { point: point } : {}),
        ...(refundable ? { refundable: refundable } : {}),
        ...(status ? { status: status } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(room ? { room: room } : {}),
        ...(keywords ? { keywords: keywords } : {}),
        ...(departure ? { departure: departure } : {}),
        ...(departureIsCity ? { departureIsCity: departureIsCity } : {}),
        ...(arrival ? { arrival: arrival } : {}),
        ...(arrivalIsCity ? { arrivalIsCity: arrivalIsCity } : {}),
        ...(oneWay ? { oneWay: oneWay } : {}),
        ...(direct ? { direct: direct } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    PackagesService: typeof PackagesService;
  }
}
